import { computed, nextTick, ref, Ref, watch, watchEffect } from 'vue'
import { PickerItemType } from '@/components/vision/next/picker-list/models'
import {
  HeadlessAutoCompleteValueType,
  useAutoCompleteProvider,
  ValueResolver,
} from '@/components/vision/next/inputs/useAutoComplete'
import { useObjectAsStack } from './useObjectAsStack'

export interface SearchOptions {
  searchText: string | null
  parentId: string | number | null
}

type PickerOptions = {
  onNavigate?: () => void
  getScrollPosition?: () => number
  scrollToPosition?: (pos: number) => void
  valueResolver?: ValueResolver
  onValueChanged?: (item?: HeadlessAutoCompleteValueType) => void
  value: Ref<string | number | null>
  useSearchItems: (options: Ref<SearchOptions>) => {
    items: Ref<Readonly<PickerItemType[]>>
    hasNextPage: Ref<boolean>
    fetchNextPage: (options?: any) => any
    isFetching: Ref<boolean>
    isFetched: Ref<boolean>
    isInitialLoading: Ref<boolean>
  }
}

type State = {
  searchText: string
  scrollPosition: number | null
  parentId: string | number | null
  parentName: string | null
}

export const useNewAutocomplete = (options: PickerOptions) => {
  const { current, pop, push, exportState, importState } = useObjectAsStack<State>({
    searchText: '',
    parentId: null,
    parentName: null,
    scrollPosition: null,
  })

  // when the user makes a selection, we export our state stack here
  // so that if the picker is reopened, we can restore the entire state
  // including the hierarchy.
  const previouslySelectedState = ref<string>()

  const search = computed(() => current.value.searchText)

  const autoComplete = useAutoCompleteProvider({
    search,
    onValueChanged: options.onValueChanged as any,
    value: options.value as any,
    valueResolver: options.valueResolver,
  })

  const searchOptions = computed<SearchOptions>(() => ({
    searchText: current.value.searchText,
    parentId: current.value.parentId,
  }))

  const {
    items: rawItems,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetched,
    isInitialLoading,
  } = options.useSearchItems(searchOptions)

  const items = ref<Readonly<PickerItemType[]>>([])
  watchEffect(() => {
    items.value = Object.freeze(rawItems.value)
  })

  // if the user is actively searching or changing
  // filters we should request to reset the scroll position
  watch(searchOptions, (newOpts, oldOpts) => {
    if (oldOpts.searchText !== newOpts.searchText) {
      options.scrollToPosition?.(0)
    }
  })

  const selectItem = (item: PickerItemType) => {
    autoComplete.addValue(item)
    current.value.scrollPosition = options.getScrollPosition?.() ?? 0
    previouslySelectedState.value = exportState()
  }

  const updateSearch = (newSearch: string) => {
    current.value.searchText = newSearch
  }

  const continueFetching = () => {
    if (hasNextPage?.value && !isFetching.value) {
      fetchNextPage()
    }
  }

  const navigateIntoItem = (item: PickerItemType) => {
    current.value.scrollPosition = options.getScrollPosition?.() ?? 0

    // push a new state onto our stack, this will update
    // the current pointer with new values, while preserving
    // the old values
    push({
      parentId: item.id,
      parentName: item.title,
      searchText: '',
      scrollPosition: 0,
    })

    // scroll back to the top and clear the items.
    // not clearing the items causes visual glitches
    // if the network or Tanstack is slow.
    options.scrollToPosition?.(0)
    items.value = []

    options.onNavigate?.()
  }

  const navigateBack = () => {
    // pop the existing state, this will update
    // the current pointer with new values
    const { next } = pop()

    nextTick().then(() => {
      options.scrollToPosition?.(next.scrollPosition ?? 0)
    })

    autoComplete.setModified(true)

    options.onNavigate?.()
  }

  const clear = () => {
    autoComplete.clear()
    previouslySelectedState.value = undefined

    current.value.searchText = ''
    current.value.scrollPosition = null
    current.value.parentId = null
    current.value.parentName = null

    options.scrollToPosition?.(0)
  }

  const noResultsMessage = computed(() => {
    if (isInitialLoading.value || !isFetched.value || items.value.length > 0) {
      return
    }
    return 'No results'
  })

  const clearSearch = () => {
    current.value.searchText = ''
  }

  const restoreToPreviousState = () => {
    if (previouslySelectedState.value) {
      const { next } = importState(previouslySelectedState.value)
      nextTick().then(() => {
        options.scrollToPosition?.(next.scrollPosition ?? 0)
      })
    }
  }
  const parentName = computed(() => current.value.parentName)

  return {
    items,
    updateSearch,
    selectItem,
    clear,
    clearSearch,
    search,
    continueFetching,
    noResultsMessage,
    navigateIntoItem,
    navigateBack,
    restoreToPreviousState,
    parentName,
  }
}
